/**
 * Purchase API
 * Gateway-agnostic payment processing and user credits.
 * Used by all charge processors (Credit Card, PayPal reference transaction, Apple Pay, Google Pay).
 */

import { browserContext, getClientPlatformForAPI } from '../utils/platform';
import { getDeviceSessionId } from '../utils/getDeviceSessionId';
import { apiRequest, ApiError } from './core';
import type { RiskModelConfig } from '../types';

// ============================================================================
// Additional Purchase Info (analytics enrichment)
// ============================================================================

interface AdditionalPurchaseInfoResponse {
  paypal_payer_id?: string;
  paypal_transaction_id?: string;
  paypal_billing_agreement_id?: string;
  paypal_invoice_id?: string;
  spending_limits?: string[];
  [key: string]: any;
}

export interface AdditionalPurchaseInfo {
  paypalPayerId?: string;
  paypalTransactionId?: string;
  paypalBillingAgreementId?: string;
  paypalInvoiceId?: string;
  spendingLimits?: string[];
}

// ============================================================================
// Types
// ============================================================================

export interface ProcessingDataParams {
  use_reference_transaction?: boolean;
  with3ds?: boolean;
  with_bonus?: boolean;
  advisor_id?: number;
  context?: string;
  platform?: string;
  /** ISO currency code for local-currency processing (e.g., 'MXN') */
  currency?: string;
  /** Exchange rate for local-currency processing */
  fx_rate?: number;
  [key: string]: any;
}

export interface ProcessingDataResponse {
  required_params: any;
  three_ds_config?: any;
  conversion_event_id?: string;
}

export interface CreatePurchaseParams {
  requiredParams: any;
  riskModel?: string | null;
  riskModelConfig?: RiskModelConfig | null;
  /** Exchange rate for local-currency processing */
  fxRate?: number;
  /** ISO currency code for local-currency processing (e.g., 'MXN') */
  currency?: string;
  [key: string]: any;
}

export interface CreatePurchaseResponse {
  id: number;
  error?: string;
  /** Error type from API (e.g., 'spending_limits') — preserved from 422 response */
  errorType?: string;
  /** Error title from API (custom heading) */
  errorTitle?: string;
  /** Whether error is recoverable (PayPal) */
  recoverable?: boolean;
  /** Whether error is retriable */
  retriable?: boolean;
  /** Error origin URL for recoverable errors */
  errorOrigin?: string;
  /** Error pathname for recoverable errors */
  errorPathname?: string;
  /** Spending limits array from API (present on spending_limits errors) */
  spendingLimits?: string[];
}

/**
 * Raw API response from GET /my/user_credits/{id} (snake_case from backend)
 */
export interface PurchaseStatusResponse {
  ok_message?: string;
  /** Localised success message for the user's display currency */
  local_ok_message?: string;
  error_message?: string;
  error_type?: string;
  error_title?: string;
  cashback_amount?: string;
  extra_event_params?: Record<string, string>;
  recoverable?: boolean;
  retriable?: boolean;
  error_origin?: string;
  error_pathname?: string;
  spending_limits?: string[];
  pathname?: string;
  threed_auth_result?: string;
  [key: string]: any;
}

/**
 * Mapped purchase status for component consumption (camelCase).
 * Produced by getPurchaseStatus after mapping snake_case fields.
 */
export interface PurchaseStatus {
  /** Already resolved to local currency when available */
  okMessage?: string;
  errorMessage?: string;
  errorType?: string;
  errorTitle?: string;
  cashbackAmount?: string;
  /** Extra analytics event params from backend (merged into Purchase/purchase failed events) */
  extraEventParams?: Record<string, string>;
  recoverable?: boolean;
  retriable?: boolean;
  errorOrigin?: string;
  errorPathname?: string;
  spendingLimits?: string[];
  /** URL for fetching additional purchase analytics (PayPal payer/transaction info) */
  pathname?: string;
  /** 3DS authentication result (e.g., 'Y', 'A') — included in Purchase events when 3DS was used */
  threeDAuthResult?: string;
  [key: string]: any;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get payment processing configuration
 *
 * Calls GET /my/app_credits/{amount}/process_credits to get:
 * - required_params: All parameters needed for POST /my/user_credits
 * - three_ds_config: 3D Secure configuration if required
 *
 * @param amount - Amount to process (price + fees, bonus handled via with_bonus flag)
 * @param params - Processing options (with_bonus, advisor_id, context, etc.)
 */
export const getProcessingData = async (
  amount: number,
  params: ProcessingDataParams
): Promise<ProcessingDataResponse> => {
  // Build query parameters with defaults
  const queryParams = new URLSearchParams();
  queryParams.append('platform', params.platform || getClientPlatformForAPI());
  queryParams.append('with_payment_sources', 'true');

  // Add other parameters if provided
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && key !== 'platform') {
      queryParams.append(key, String(value));
    }
  });

  const path = `/my/app_credits/${ amount }/process_credits?${ queryParams.toString() }`;

  return apiRequest<ProcessingDataResponse>(path);
};

/**
 * Create purchase
 *
 * Flow:
 * 1. Takes requiredParams from /process_credits API
 * 2. Adds browser_context for fraud detection
 * 3. Generates device_session_id from Risk.js if risk model is available
 * 4. Sends POST /my/user_credits to create the purchase
 *
 * Returns purchase ID on success, or error message if API returns 422 validation error
 */
export const createPurchase = async (
  params: CreatePurchaseParams
): Promise<CreatePurchaseResponse> => {
  // Build request body: required params from API, browser context, platform, device session ID
  const requestBody: Record<string, unknown> = {
    ...params.requiredParams,
    ...browserContext,
    platform: getClientPlatformForAPI(),
    ...(params.currency && { currency: params.currency }),
    ...(params.fxRate && { fx_rate: params.fxRate })
  };

  try {
    // Generate device_session_id for fraud detection if risk model is configured
    if (params.riskModel && params.riskModelConfig) {
      const deviceSessionId = await getDeviceSessionId(params.riskModel, params.riskModelConfig);
      if (deviceSessionId) {
        requestBody.device_session_id = deviceSessionId;
      }
    }

    return await apiRequest<CreatePurchaseResponse>(
      '/my/user_credits',
      {
        method: 'POST',
        body: requestBody
      }
    );
  } catch (error: unknown) {
    // Handle 422 validation errors (e.g., spending limits, account restrictions) -
    // return error data instead of throwing so callers can surface it on click.
    if (error instanceof ApiError && (error.errorData?.error_message || error.errorData?.error)) {
      const data = error.errorData;
      return {
        id: data.id ?? 0,
        error: error.userMessage,
        errorType: data.error_type,
        errorTitle: data.error_title,
        recoverable: data.recoverable,
        retriable: data.retriable,
        errorOrigin: data.error_origin,
        errorPathname: data.error_pathname,
        spendingLimits: data.spending_limits as string[] | undefined
      };
    }
    // Re-throw network errors and other unexpected errors
    throw error;
  }
};

/**
 * Get purchase status (polling)
 *
 * Maps snake_case API response fields to camelCase for component consumption.
 */
export const getPurchaseStatus = async (
  userCreditId: number,
  platform?: string
): Promise<PurchaseStatus> => {
  const queryParams = platform ? `?platform=${ encodeURIComponent(platform) }` : '';
  const response = await apiRequest<PurchaseStatusResponse>(
    `/my/user_credits/${ userCreditId }${ queryParams }`
  );

  return {
    okMessage: response.local_ok_message ?? response.ok_message,
    errorMessage: response.error_message,
    errorType: response.error_type,
    errorTitle: response.error_title,
    cashbackAmount: response.cashback_amount,
    extraEventParams: response.extra_event_params,
    recoverable: response.recoverable,
    retriable: response.retriable,
    errorOrigin: response.error_origin,
    errorPathname: response.error_pathname,
    spendingLimits: response.spending_limits,
    pathname: response.pathname,
    threeDAuthResult: response.threed_auth_result
  };
};

/**
 * Fetch additional purchase info (PayPal payer/transaction details) from a purchase URL.
 * Matches old getAdditionalPurchaseInfoFromURL in @common/api/api.
 * Response is mapped from snake_case to camelCase for parity with old mbwFetch behavior.
 */
export const getAdditionalPurchaseInfo = async (
  purchaseURL: string
): Promise<AdditionalPurchaseInfo> => {
  const response = await apiRequest<AdditionalPurchaseInfoResponse>(purchaseURL);
  return {
    paypalPayerId: response.paypal_payer_id,
    paypalTransactionId: response.paypal_transaction_id,
    paypalBillingAgreementId: response.paypal_billing_agreement_id,
    paypalInvoiceId: response.paypal_invoice_id,
    spendingLimits: response.spending_limits
  };
};
